import isObject from 'lodash-es/isObject';
import { useMutation } from 'react-query';
import { createStripeAccount, getStripeOnboardingLink } from '@frontend/api-payments';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';

const stripeRedirectUrl = `${window.location.origin}/payments/invoices#settings/payments/account-info?stripe_redirected=1`;

interface useStripeSignupProps {
  locationId: string;
  onCreateError?: () => void;
  onRedirect?: () => void;
  refetch?: () => void;
}
export const useStripeOnboarding = ({ locationId, onCreateError, onRedirect, refetch }: useStripeSignupProps) => {
  const { t } = useTranslation('payments');
  const alert = useAlert();

  const redirectToStripe = ({
    onboardingUrl,
    setLoading,
  }: {
    onboardingUrl: string;
    setLoading?: (loading: boolean) => void;
  }) => {
    window.open(onboardingUrl, '_blank');
    setLoading?.(false);
    onRedirect?.();
  };

  const { mutate: createStripeAccountMutation } = useMutation(
    ({ businessType }: { businessType: string }) =>
      createStripeAccount(businessType, false, locationId || '')
        .then((res) => getStripeOnboardingLink(res.stripeId, stripeRedirectUrl, stripeRedirectUrl, locationId || ''))
        .then((response) => response),
    { retry: false }
  );
  const { mutate: continueOnboardingMutation } = useMutation(
    ({ stripeId }: { stripeId: string | undefined }) =>
      getStripeOnboardingLink(stripeId, stripeRedirectUrl, stripeRedirectUrl, locationId || '').then(
        (response) => response
      ),
    { retry: false }
  );

  const onError = (e: unknown) => {
    let error = t('Failed to redirect to Stripe. Please try again.');
    if (isObject(e) && 'data' in e) {
      const data = (e as { data: { error?: string; message?: string } }).data;
      error = data.error || data.message?.replaceAll('_', ' ') || error;
    }
    alert.error(error);
    onCreateError?.();
  };

  const onCreateAccountClick = ({
    businessType,
    setLoading,
  }: {
    businessType: string;
    setLoading?: (loading: boolean) => void;
  }) => {
    setLoading?.(true);
    const onSuccess = ({ next: onboardingUrl }: { next: string }) => {
      redirectToStripe({ onboardingUrl, setLoading });
      refetch?.();
    };
    createStripeAccountMutation({ businessType }, { onSuccess, onError });
  };

  const onContinueOnboardClick = ({
    stripeId,
    setLoading,
  }: {
    stripeId: string;
    setLoading?: (loading: boolean) => void;
  }) => {
    setLoading?.(true);
    const onSuccess = ({ next: onboardingUrl }: { next: string }) => {
      redirectToStripe({ onboardingUrl, setLoading });
      refetch?.();
    };
    continueOnboardingMutation({ stripeId }, { onSuccess, onError });
  };

  return { onCreateAccountClick, onContinueOnboardClick };
};
